// @ts-nocheck
import { useState } from 'react';
import { Avatar, Badge, Button, Card, Code, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { mockUsers, mockProjects, mockTasks, getProjectsByOwnerId, getTasksByAssigneeId } from '../mockData';

export default function MembersPage() {
  const [selectedMember, setSelectedMember] = useState(mockUsers[0]);
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null);

  const departments = Array.from(new Set(mockUsers.map((user) => user.department)));

  const filteredMembers = departmentFilter
    ? mockUsers.filter((user) => user.department === departmentFilter)
    : mockUsers;

  const membersByDepartment = departments.map((dept) => ({
    department: dept,
    count: mockUsers.filter((u) => u.department === dept).length,
  }));

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Members Domain Example</Title>
        <Text c="dimmed" size="sm">
          Real-world example of a team directory using components with relational mock data
        </Text>
      </Stack>

      {/* Overview */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Team Directory Overview</Title>
          <Text size="sm" c="dimmed">
            This example demonstrates a complete team directory interface using Cards, Avatars, Badges, and relational data from our mock data system.
          </Text>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Features:</Text>
            <Text size="xs" c="dimmed" component="div">
              • 30 team members with complete profiles
              <br />
              • Department organization and filtering
              <br />
              • Role and status tracking
              <br />
              • Skills and expertise display
              <br />
              • Real data relationships showing member projects and tasks
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Department Summary */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>Department Overview</Title>
            <Button
              variant={departmentFilter === null ? 'filled' : 'light'}
              size="sm"
              onClick={() => setDepartmentFilter(null)}
            >
              All Members ({mockUsers.length})
            </Button>
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
            {membersByDepartment.map(({ department, count }) => (
              <Card
                key={department}
                variant={departmentFilter === department ? 'filled' : 'subtle'}
                p="md"
                style={{ cursor: 'pointer' }}
                onClick={() => setDepartmentFilter(departmentFilter === department ? null : department)}
              >
                <Stack gap="xs" align="center">
                  <Text size="xl" fw={700}>
                    {count}
                  </Text>
                  <Text size="sm" ta="center" lineClamp={1}>
                    {department}
                  </Text>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        </Stack>
      </Card>

      {/* Member Grid */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>
              {departmentFilter ? `${departmentFilter} Team` : 'All Team Members'} ({filteredMembers.length})
            </Title>
            <Button variant="gradient" gradientVariant="primary" size="sm">
              + Add Member
            </Button>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {filteredMembers.slice(0, 12).map((member) => {
              const memberProjects = getProjectsByOwnerId(member.id, mockProjects);
              const memberTasks = getTasksByAssigneeId(member.id, mockTasks);

              return (
                <Card
                  key={member.id}
                  variant="elevated"
                  p="md"
                  hoverEffect="lift"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedMember(member)}
                >
                  <Stack gap="sm">
                    <Group gap="md">
                      <Avatar src={member.avatar} size="lg" />
                      <Stack gap={4} style={{ flex: 1 }}>
                        <Text fw={600} lineClamp={1}>
                          {member.name}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {member.title}
                        </Text>
                        <Badge
                          size="xs"
                          color={
                            member.status === 'active'
                              ? 'green'
                              : member.status === 'away'
                              ? 'yellow'
                              : member.status === 'busy'
                              ? 'orange'
                              : 'gray'
                          }
                          variant="dot"
                        >
                          {member.status || 'offline'}
                        </Badge>
                      </Stack>
                    </Group>

                    <Stack gap="xs">
                      <Group gap="xs">
                        <Badge size="sm" variant="light" color="blue">
                          {member.department}
                        </Badge>
                        <Badge size="sm" variant="outline">
                          {member.role}
                        </Badge>
                      </Group>

                      {member.location && (
                        <Text size="xs" c="dimmed">
                          📍 {member.location}
                        </Text>
                      )}
                    </Stack>

                    <Group justify="space-between">
                      <Stack gap={4}>
                        <Text size="xs" c="dimmed">
                          Projects
                        </Text>
                        <Text size="sm" fw={600}>
                          {memberProjects.length}
                        </Text>
                      </Stack>

                      <Stack gap={4} align="flex-end">
                        <Text size="xs" c="dimmed">
                          Tasks
                        </Text>
                        <Text size="sm" fw={600}>
                          {memberTasks.length}
                        </Text>
                      </Stack>
                    </Group>

                    {member.skills && member.skills.length > 0 && (
                      <Group gap="xs">
                        {member.skills.slice(0, 2).map((skill) => (
                          <Badge key={skill} size="xs" variant="dot" color="violet">
                            {skill}
                          </Badge>
                        ))}
                        {member.skills.length > 2 && (
                          <Text size="xs" c="dimmed">
                            +{member.skills.length - 2}
                          </Text>
                        )}
                      </Group>
                    )}
                  </Stack>
                </Card>
              );
            })}
          </SimpleGrid>
        </Stack>
      </Card>

      {/* Selected Member Detail */}
      {selectedMember && (
        <Card withBorder p="lg">
          <Stack gap="md">
            <Title order={4}>Member Profile</Title>

            <Card variant="gradient" gradientVariant="primary" p="lg">
              <Stack gap="md">
                <Group gap="lg" align="flex-start">
                  <Avatar src={selectedMember.avatar} size={80} />
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Group justify="space-between">
                      <Stack gap={4}>
                        <Text c="white" fw={700} size="xl">
                          {selectedMember.name}
                        </Text>
                        <Text c="white" opacity={0.9} size="sm">
                          {selectedMember.title}
                        </Text>
                      </Stack>
                      <Badge
                        size="lg"
                        color={
                          selectedMember.status === 'active'
                            ? 'green'
                            : selectedMember.status === 'away'
                            ? 'yellow'
                            : selectedMember.status === 'busy'
                            ? 'orange'
                            : 'gray'
                        }
                      >
                        {selectedMember.status || 'offline'}
                      </Badge>
                    </Group>

                    {selectedMember.bio && (
                      <Text c="white" opacity={0.9} size="sm">
                        {selectedMember.bio}
                      </Text>
                    )}
                  </Stack>
                </Group>

                <Group gap="lg">
                  <Stack gap={4}>
                    <Text c="white" opacity={0.7} size="xs">
                      Department
                    </Text>
                    <Text c="white" fw={600}>
                      {selectedMember.department}
                    </Text>
                  </Stack>
                  <Stack gap={4}>
                    <Text c="white" opacity={0.7} size="xs">
                      Role
                    </Text>
                    <Text c="white" fw={600}>
                      {selectedMember.role}
                    </Text>
                  </Stack>
                  {selectedMember.location && (
                    <Stack gap={4}>
                      <Text c="white" opacity={0.7} size="xs">
                        Location
                      </Text>
                      <Text c="white" fw={600}>
                        {selectedMember.location}
                      </Text>
                    </Stack>
                  )}
                  <Stack gap={4}>
                    <Text c="white" opacity={0.7} size="xs">
                      Joined
                    </Text>
                    <Text c="white" fw={600}>
                      {selectedMember.joinedAt.toLocaleDateString()}
                    </Text>
                  </Stack>
                </Group>
              </Stack>
            </Card>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <Card variant="subtle" p="md">
                <Stack gap="sm">
                  <Text fw={600}>Contact Information</Text>
                  <Stack gap="xs">
                    <Group gap="xs">
                      <Text size="xs" c="dimmed" w={60}>
                        Email:
                      </Text>
                      <Text size="sm">{selectedMember.email}</Text>
                    </Group>
                    {selectedMember.phone && (
                      <Group gap="xs">
                        <Text size="xs" c="dimmed" w={60}>
                          Phone:
                        </Text>
                        <Text size="sm">{selectedMember.phone}</Text>
                      </Group>
                    )}
                  </Stack>
                </Stack>
              </Card>

              <Card variant="subtle" p="md">
                <Stack gap="sm">
                  <Text fw={600}>Work Summary</Text>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Projects Owned
                      </Text>
                      <Badge>{getProjectsByOwnerId(selectedMember.id, mockProjects).length}</Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Active Tasks
                      </Text>
                      <Badge>
                        {getTasksByAssigneeId(selectedMember.id, mockTasks).filter((t) => t.status !== 'done').length}
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Completed Tasks
                      </Text>
                      <Badge color="green">
                        {getTasksByAssigneeId(selectedMember.id, mockTasks).filter((t) => t.status === 'done').length}
                      </Badge>
                    </Group>
                  </Stack>
                </Stack>
              </Card>
            </SimpleGrid>

            {selectedMember.skills && selectedMember.skills.length > 0 && (
              <Card variant="outline" p="md">
                <Stack gap="xs">
                  <Text fw={600}>Skills & Expertise</Text>
                  <Group gap="xs">
                    {selectedMember.skills.map((skill) => (
                      <Badge key={skill} variant="light" color="violet">
                        {skill}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              </Card>
            )}

            {getProjectsByOwnerId(selectedMember.id, mockProjects).length > 0 && (
              <Card variant="outline" p="md">
                <Stack gap="sm">
                  <Text fw={600}>Projects ({getProjectsByOwnerId(selectedMember.id, mockProjects).length})</Text>
                  <Stack gap="xs">
                    {getProjectsByOwnerId(selectedMember.id, mockProjects)
                      .slice(0, 3)
                      .map((project) => (
                        <Group key={project.id} justify="space-between">
                          <Stack gap={0}>
                            <Text size="sm" fw={500}>
                              {project.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {project.category}
                            </Text>
                          </Stack>
                          <Badge
                            color={
                              project.status === 'completed'
                                ? 'green'
                                : project.status === 'in-progress'
                                ? 'blue'
                                : 'gray'
                            }
                          >
                            {project.status}
                          </Badge>
                        </Group>
                      ))}
                    {getProjectsByOwnerId(selectedMember.id, mockProjects).length > 3 && (
                      <Text size="xs" c="dimmed">
                        +{getProjectsByOwnerId(selectedMember.id, mockProjects).length - 3} more projects
                      </Text>
                    )}
                  </Stack>
                </Stack>
              </Card>
            )}
          </Stack>
        </Card>
      )}

      {/* Code Example */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Implementation Code</Title>
          <Text size="sm" c="dimmed">
            This example shows how to use relational mock data with UI components
          </Text>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Data Structure:</Text>
            <Code block language="typescript">
{`// Import mock data and helpers
import { mockUsers, mockProjects, mockTasks, getProjectsByOwnerId, getTasksByAssigneeId } from './mockData';

// Access member with relational data
const member = mockUsers[0];
const memberProjects = getProjectsByOwnerId(member.id, mockProjects); // Get all projects owned
const memberTasks = getTasksByAssigneeId(member.id, mockTasks); // Get all assigned tasks`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Component Usage:</Text>
            <Code block language="tsx">
{`<Card variant="elevated" p="md">
  <Stack gap="sm">
    {/* Member Header */}
    <Group gap="md">
      <Avatar src={member.avatar} size="lg" />
      <Stack gap={4}>
        <Text fw={600}>{member.name}</Text>
        <Text size="xs" c="dimmed">{member.title}</Text>
        <Badge size="xs" color="green" variant="dot">
          {member.status}
        </Badge>
      </Stack>
    </Group>

    {/* Department and Role */}
    <Group gap="xs">
      <Badge variant="light" color="blue">{member.department}</Badge>
      <Badge variant="outline">{member.role}</Badge>
    </Group>

    {/* Work Stats */}
    <Group justify="space-between">
      <Stack gap={4}>
        <Text size="xs" c="dimmed">Projects</Text>
        <Text size="sm" fw={600}>{memberProjects.length}</Text>
      </Stack>
      <Stack gap={4}>
        <Text size="xs" c="dimmed">Tasks</Text>
        <Text size="sm" fw={600}>{memberTasks.length}</Text>
      </Stack>
    </Group>

    {/* Skills */}
    <Group gap="xs">
      {member.skills?.map((skill) => (
        <Badge key={skill} size="xs" variant="dot" color="violet">
          {skill}
        </Badge>
      ))}
    </Group>
  </Stack>
</Card>`}
            </Code>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
